import { CommonModule, DatePipe } from '@angular/common';
import { Component, Input, OnInit, inject, signal } from '@angular/core';
import { RouterModule } from '@angular/router';
import { finalize } from 'rxjs';
import { ExpenseApiService } from '../../../core/api-services/expense-api.service';
import { ExpenseOverview } from '../../../core/models/expense';

/**
 * The student's funding picture, shown on the Budget page.
 *
 * The same data backs two tabs: `budget` shows the allocation and how much of
 * each benefit is left, `expenses` shows how those expenses are actually paid
 * (claimed by the student, or settled with the provider for them).
 */
@Component({
  selector: 'app-student-expenses',
  standalone: true,
  imports: [CommonModule, DatePipe, RouterModule],
  templateUrl: './expenses.html',
  styleUrl: './expenses.scss',
})
export class StudentExpensesComponent implements OnInit {
  private readonly api = inject(ExpenseApiService);

  /** Which tab of the Budget page this instance is rendering. */
  @Input() mode: 'budget' | 'expenses' = 'budget';

  readonly overview = signal<ExpenseOverview | null>(null);
  readonly loading = signal(false);
  readonly error = signal('');

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.loading.set(true);
    this.error.set('');

    this.api
      .getMyExpenses()
      .pipe(finalize(() => this.loading.set(false)))
      .subscribe({
        next: (overview) => this.overview.set(overview),
        error: () => this.error.set('Your expenses could not be loaded.'),
      });
  }

  /** Payments still awaiting settlement (scheduled or past due). */
  get outstandingPayments() {
    return (this.overview()?.scheduledPayments ?? []).filter(
      (payment) => payment.status === 'SCHEDULED' || payment.status === 'OVERDUE'
    );
  }

  /** Benefit categories, ordered so the claimable ones come first. */
  get categories() {
    return [...(this.overview()?.categories ?? [])].sort((left, right) => {
      if (left.payDirectToProvider !== right.payDirectToProvider) {
        return left.payDirectToProvider ? 1 : -1;
      }

      return left.name.localeCompare(right.name);
    });
  }

  progressWidth(percent: number): string {
    return `${Math.min(100, Math.max(0, percent ?? 0))}%`;
  }

  money(value: number | null | undefined, currency: string | null | undefined): string {
    return `${(value ?? 0).toFixed(2)} ${currency ?? ''}`.trim();
  }

  /** Maps a payment status onto the shared `.status` pill classes. */
  statusClass(status: string): string {
    switch (status) {
      case 'PAID':
        return 'status status-approved';
      case 'OVERDUE':
        return 'status status-rejected';
      case 'CANCELLED':
        return 'status status-cancelled';
      default:
        return 'status status-pending';
    }
  }
}
